import React, { useState } from 'react'

import { authedPost } from '../lib/website_service'
import { getMetadata } from '../lib/outlook-msg'

import './Dropbox.css'

type Job = {
  jobid: string
  email: string
  doNotShortlist: boolean
}

const RESULT_MESSAGES: Record<string, string> = {
  CANDIDATE_NOT_FOUND: 'Candidate not found.',
  NO_JOB_ID: 'You must supply a job ID.',
  JOB_NOT_FOUND:
    'Cannot find a job with that ID. Please verify this is the correct 4-digit code in Bullhorn and the job is published to the website.',
  EXISTS_IN_QUEUE:
    'Candidate  CV has already been dropped for this job, and is currently queued for Shortlisting in Bullhorn.',
  OK: 'Candidate successfully submitted for job.',
  OK_NO_SHORTLIST: 'Candidate CV successfully parsed without shortlisting.',
  NO_RESUME_ATTACHMENT:
    'This mail does not contain a PDF, DOC or DOCX resume attachment to parse.',
}

const waitForNextPaint = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve())
  })

export const Dropbox = () => {
  const [showModal, setShowModal] = useState(false)
  const [job, setJob] = useState<Job>({
    jobid: '',
    email: '',
    doNotShortlist: false,
  })
  const [file, setFile] = useState<File>()
  const [result, setResult] = useState('')
  const [statusMessage, setStatusMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const submitApplication = async (
    jobId: string,
    email: string,
    file: File,
    doNotShortlist: boolean
  ) => {
    setShowModal(false)
    setResult('')
    setStatusMessage('Processing CV...')
    setIsSubmitting(true)
    await waitForNextPaint()

    try {
      const formData = new FormData()
      formData.append('jobid', jobId)
      formData.append('email', email)
      formData.append('file', file)
      formData.append('doNotShortlist', String(doNotShortlist))

      const result = await authedPost(`internal/application`, formData)
      const json = await result.json()
      setResult(json.result)
      setTimeout(() => {
        setResult('')
      }, 3000)
    } catch (e: any) {
      alert(e.message)
    } finally {
      setStatusMessage('')
      setIsSubmitting(false)
    }
  }

  const onDragOver = (event: any) => {
    event.preventDefault()
  }

  const resubmitJob = async () => {
    if (file) {
      await submitApplication(job.jobid, job.email, file, job.doNotShortlist)
    }
  }

  const cancelJobDetails = () => {
    setShowModal(false)
    setFile(undefined)
    setJob({
      jobid: '',
      email: '',
      doNotShortlist: false,
    })
  }

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault()
    try {
      setResult('')
      setStatusMessage('Reading file...')
      if (event.dataTransfer.files.length !== 1) {
        throw new Error(`Must drop exactly one file`)
      }
      const file = event.dataTransfer.files[0]
      if (file.name.indexOf('.') === -1) {
        throw new Error(
          `You must drag a file with a valid file extension into the dropbox: .eml, .doc(x), .pdf`
        )
      }
      setFile(file)
      const fileReader = new FileReader()
      fileReader.onload = () => {
        const fileData = fileReader.result
        const { jobId, email } = getMetadata(fileData as ArrayBuffer)
        if (!jobId || !email) {
          setJob({
            jobid: jobId || '',
            email: email || '',
            doNotShortlist: false,
          })
          setStatusMessage('')
          setShowModal(true)
          return
        }
        submitApplication(jobId, email, file, false)
      }
      fileReader.readAsArrayBuffer(file)
    } catch (e: any) {
      setStatusMessage('')
      alert(e.message)
    }
  }

  return (
    <div className="dropbox-background">
      <div className="dropbox-title">DROPBOX</div>
      <div className="dropbox-container">
        {result && (
          <div className="dropbox-modal-overlay">
            <div className="dropbox-modal">
              <p>{RESULT_MESSAGES[result]}</p>
            </div>
          </div>
        )}
        {showModal && (
          <div className="dropbox-modal-overlay">
            <div className="dropbox-modal">
              <p>Enter the CV details</p>
              <label className="dropbox-checkbox" htmlFor="do_not_shortlist">
                <input
                  id="do_not_shortlist"
                  type="checkbox"
                  checked={job.doNotShortlist}
                  onChange={(e) =>
                    setJob({ ...job, doNotShortlist: e.target.checked })
                  }
                />
                Do not shortlist to a job
              </label>
              <label htmlFor="job_id">
                Bullhorn Job ID:
                <input
                  id="job_id"
                  placeholder="5500"
                  value={job.jobid}
                  disabled={job.doNotShortlist}
                  onChange={(e) => setJob({ ...job, jobid: e.target.value })}
                />
              </label>
              <label htmlFor="email">
                Candidate email:
                <input
                  id="email"
                  placeholder="bob@example.com"
                  value={job.email}
                  onChange={(e) => setJob({ ...job, email: e.target.value })}
                />
              </label>
              <div className="dropbox-modal-actions">
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => cancelJobDetails()}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={
                    isSubmitting ||
                    !job.email ||
                    (!job.doNotShortlist && !job.jobid)
                  }
                  onClick={() => resubmitJob()}
                >
                  {isSubmitting ? 'Processing...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="drop-target" onDragOver={onDragOver} onDrop={onDrop}>
          Drop mails or CVs here
        </div>
        {statusMessage && (
          <div className="dropbox-modal-overlay">
            <div className="dropbox-status">
              <span className="dropbox-spinner" />
              <span>{statusMessage}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
